import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, InjectionToken, Injector } from '@angular/core';

import { toAppError } from '../../../shared/utils/app-error.util';
import {
  browserChunkFailureDeps,
  type ChunkFailureDeps,
  handleChunkFailure,
  isChunkLoadError,
} from '../../../shared/utils/chunk-load-recovery.util';
import { GENERIC_ERROR_KEY, STALE_APP_KEY } from '../../../shared/utils/error-messages';
import { translateNow } from '../i18n/translate-now';
import { ToastNotificationService } from './toast-notification.service';

/** At most one toast per this window; errors inside it are logged but their toast is dropped. */
export const UNEXPECTED_TOAST_WINDOW_MS = 5_000;

/** Browser-dependent inputs of {@link GlobalErrorHandler}, replaceable in specs. */
export interface GlobalErrorEnvironment {
  /** Chunk-reload dependencies (storage, clock, reload, page start). */
  chunk: ChunkFailureDeps;
  /** The router URL (path, query and fragment) the failing navigation targeted. */
  currentUrl: () => string;
}

/** Provides the {@link GlobalErrorEnvironment}; the default reads the real browser. */
export const GLOBAL_ERROR_ENVIRONMENT = new InjectionToken<GlobalErrorEnvironment>(
  'GLOBAL_ERROR_ENVIRONMENT',
  {
    providedIn: 'root',
    factory: (): GlobalErrorEnvironment => ({
      chunk: browserChunkFailureDeps(),
      currentUrl: () => `${window.location.pathname}${window.location.search}${window.location.hash}`,
    }),
  },
);

/**
 * Whether `error` is the Monaco diff editor teardown noise raised by `sk-diff-editor`, which
 * disposes its text models before resetting the widget. Cosmetic: the editor still disposes fully.
 *
 * TODO(stark-ui): remove once `SkDiffEditorComponent.ngOnDestroy` calls `setModel(null)` first.
 *
 * @example
 * isMonacoDiffDisposeError(new Error('TextModel got disposed before DiffEditorWidget model got reset')); // true
 */
export function isMonacoDiffDisposeError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('TextModel got disposed before DiffEditorWidget');
}

/**
 * Generates a local reference to quote to support: 8 lowercase hexadecimal characters.
 *
 * @example
 * newErrorReference(); // 'a3f09c1e'
 */
export function newErrorReference(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Last line of defence for failures nobody handled (`ErrorHandler` and `unhandledrejection`).
 * Logs the error with a local reference and shows one toast `Ocurrió un error inesperado.` with
 * `Referencia: <id>`, at most once per {@link UNEXPECTED_TOAST_WINDOW_MS}. Stale-chunk failures go
 * through the existing reload recovery and only tell the user when the same URL fails again within
 * 10 seconds of the automatic reload. Never throws.
 */
@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly env = inject(GLOBAL_ERROR_ENVIRONMENT);
  private lastToastAt = Number.NEGATIVE_INFINITY;
  /** The zone reports a rejection both to the `ErrorHandler` and as a window event; report it once. */
  private readonly reported = new WeakSet<object>();

  /** Entry point of Angular's `ErrorHandler` contract. */
  handleError(error: unknown): void {
    try {
      this.report(error);
    } catch (internal) {
      // The handler must never throw: that would hide the original failure and can loop.
      try {
        console.error('[GlobalErrorHandler] failed while reporting an error', internal);
      } catch {
        // Nothing left to do.
      }
    }
  }

  /**
   * Starts listening to `unhandledrejection` on `target` (promises rejected outside Angular's
   * zone never reach `ErrorHandler`).
   *
   * @returns A function that removes the listener.
   */
  listenToRejections(target: EventTarget): () => void {
    const listener = (event: Event): void => this.handleError('reason' in event ? event.reason : event);
    target.addEventListener('unhandledrejection', listener);
    return () => target.removeEventListener('unhandledrejection', listener);
  }

  private report(error: unknown): void {
    if (typeof error === 'object' && error !== null) {
      if (this.reported.has(error)) return;
      this.reported.add(error);
    }

    if (isChunkLoadError(error)) {
      this.reportChunkFailure(error);
      return;
    }

    if (isMonacoDiffDisposeError(error)) {
      console.debug('[Ignored] Monaco diff editor disposed its models early (stark-ui)');
      return;
    }

    const reference = newErrorReference();
    // An HttpErrorResponse carries the request, headers and body (tokens included): log the
    // normalised, redacted summary instead.
    console.error(`[Error ${reference}]`, error instanceof HttpErrorResponse ? toAppError(error) : error);
    this.toastOnce(translateNow('errors.withReference', { message: translateNow(GENERIC_ERROR_KEY), reference }));
  }

  private reportChunkFailure(error: unknown): void {
    const url = this.env.currentUrl();
    const outcome = handleChunkFailure(url, this.env.chunk);
    console.warn(`[ChunkLoad] ${outcome} for ${url}`, error);
    if (outcome === 'repeated') this.toastOnce(translateNow(STALE_APP_KEY));
  }

  private toastOnce(message: string): void {
    const now = Date.now();
    if (now - this.lastToastAt < UNEXPECTED_TOAST_WINDOW_MS) return;
    this.lastToastAt = now;
    // Resolved lazily: the handler is created during bootstrap, before the toast stack exists.
    this.injector.get(ToastNotificationService).error(message);
  }
}
